<template>
  <div>
    <dashboard-header :text="$t('nav.nurseCallHistory') + ' - ' + $t('nurseCall.detail')" :on-back="onGoBack"/>

    <div class="d-flex align-content-center justify-content-center">
      <v-card
          elevation="0"
          :loading="loading"
          class="card-border-10 mx-16 w-75">
        
        <!-- Basic Information -->
        <div class="pa-5">
          <h3 class="mb-4">{{ $t('nurseCall.basicInfo') }}</h3>
          <data-detail
              header-colon
              :items="basicItems"
              :simple-header="header"
          />
        </div>

        <v-divider/>

        <!-- Notification Details -->
        <div class="pa-5" v-if="detail && detail.notificationDetails">
          <h3 class="mb-4">{{ $t('nurseCall.notificationDetails') }}</h3>
          <v-data-table
              :headers="notificationHeaders"
              :items="detail.notificationDetails"
              :loading="loading"
              hide-default-footer
              class="elevation-0">
            
            <template v-slot:item.response="{ item }">
              <v-chip
                  small
                  :color="getResponseColor(item.response)"
                  text-color="white">
                {{ getResponseText(item.response) }}
              </v-chip>
            </template>

            <template v-slot:item.confirmTime="{ item }">
              {{ item.confirmTime ? formatDateTime(item.confirmTime) : '-' }}
            </template>

          </v-data-table>
        </div>

        <v-divider/>

        <!-- Summary -->
        <div class="pa-5" v-if="detail && detail.notificationSummary">
          <h3 class="mb-4">{{ $t('nurseCall.summary') }}</h3>
          <v-row>
            <v-col cols="6" sm="3">
              <v-card class="text-center pa-3" color="blue lighten-4">
                <div class="text-h6">{{ detail.notificationSummary.totalSent || 0 }}</div>
                <div class="text-caption">{{ $t('nurseCall.totalSent') }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="text-center pa-3" color="green lighten-4">
                <div class="text-h6">{{ detail.notificationSummary.totalAccepted || 0 }}</div>
                <div class="text-caption">{{ $t('nurseCall.totalAccepted') }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="text-center pa-3" color="red lighten-4">
                <div class="text-h6">{{ detail.notificationSummary.totalCancelled || 0 }}</div>
                <div class="text-caption">{{ $t('nurseCall.totalCancelled') }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="text-center pa-3" color="orange lighten-4">
                <div class="text-h6">{{ detail.notificationSummary.totalPending || 0 }}</div>
                <div class="text-caption">{{ $t('nurseCall.totalPending') }}</div>
              </v-card>
            </v-col>
          </v-row>
        </div>

      </v-card>
    </div>

  </div>
</template>

<script>
import {API2} from "../../api";
import DataDetail from "../base/DataDetail.vue";
import DashboardHeader from "../component/DashboardHeader";
import i18n from "../../lang/i18n";
import mixin from "../../mixin";

export default {
  components: {DashboardHeader, DataDetail},
  mixins: [mixin],
  props: {
    nurseCallId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      detail: null,
    };
  },
  mounted() {
    // Get data from sessionStorage
    const storedData = sessionStorage.getItem(`nurseCall_${this.nurseCallId}`)
    if (storedData) {
      this.detail = JSON.parse(storedData)
    } else {
      // Fallback: redirect back to list since no detail API exists
      this.$router.push('/nurse-call-history')
    }
  },
  beforeDestroy() {
    // Clean up sessionStorage when leaving component
    sessionStorage.removeItem(`nurseCall_${this.nurseCallId}`)
  },
  computed: {
    header() {
      return this.detail ? `${this.$t('nurseCall.callNumber')}${this.detail.nurseCallId}` : ''
    },
    notificationHeaders() {
      return [
        { text: this.$t('nurseCall.nurse'), value: 'name' },
        { text: this.$t('nurseCall.phoneNumber'), value: 'phoneNumber' },
        { text: this.$t('nurseCall.department'), value: 'departmentName' },
        { text: this.$t('nurseCall.response'), value: 'response' },
        { text: this.$t('nurseCall.responseTime'), value: 'confirmTime' },
      ];
    },
    basicItems() {
      if (!this.detail) return []
      
      return [
        {
          title: "ID",
          data: this.detail.nurseCallId || ""
        },
        {
          title: this.$t('nurseCall.room'),
          data: this.detail.roomNumber || ""
        },
        {
          title: this.$t('nurseCall.bed'),
          data: this.detail.bedNumber || ""
        },
        {
          title: this.$t('nurseCall.priority'),
          data: this.getPriorityText(this.detail.priority)
        },
        {
          title: this.$t('nurseCall.callType'),
          data: this.getCallTypeText(this.detail.callType)
        },
        {
          title: this.$t('nurseCall.status'),
          data: this.getStatusText(this.detail.status)
        },
        {
          title: this.$t('nurseCall.creator'),
          data: this.detail.createdBy ? this.detail.createdBy.name : ""
        },
        {
          title: this.$t('nurseCall.creatorDepartment'),
          data: this.detail.createdBy ? this.detail.createdBy.departmentName : ""
        },
        {
          title: this.$t('nurseCall.acceptedBy'),
          data: this.detail.acceptedBy ? this.detail.acceptedBy.name : this.$t('nurseCall.notYet')
        },
        {
          title: this.$t('nurseCall.createdTime'),
          data: this.detail.createdAt ? this.formatDateTime(this.detail.createdAt) : ""
        },
        {
          title: this.$t('nurseCall.acceptedTime'),
          data: this.detail.acceptedAt ? this.formatDateTime(this.detail.acceptedAt) : this.$t('nurseCall.notYet')
        },
        {
          title: this.$t('nurseCall.completedTime'),
          data: this.detail.completedAt ? this.formatDateTime(this.detail.completedAt) : this.$t('nurseCall.notYet')
        }
      ];
    },
  },
  methods: {
    onGoBack() {
      this.$router.go(-1)
    },
    getPriorityText(priority) {
      return priority ? this.$t("nurseCall.priorityYes") : this.$t("nurseCall.priorityNo");
    },
    getCallTypeText(callType) {
      switch(callType?.toLowerCase()) {
        case 'call':
          return this.$t("nurseCall.callTypeCall");
        case 'present':
          return this.$t("nurseCall.callTypePresent");
        case 'clear':
          return this.$t("nurseCall.callTypeClear");
        default:
          return callType || "";
      }
    },
    getStatusText(status) {
      switch(status?.toLowerCase()) {
        case 'pending':
          return this.$t("nurseCall.statusPending");
        case 'accepted':
          return this.$t("nurseCall.statusAccepted");
        case 'present':
          return this.$t("nurseCall.statusPresent");
        case 'completed':
          return this.$t("nurseCall.statusCompleted");
        case 'cancelled':
          return this.$t("nurseCall.statusCancelled");
        case 'missed':
          return this.$t("nurseCall.statusMissed");
        default:
          return status || "";
      }
    },
    getResponseText(response) {
      switch(response?.toLowerCase()) {
        case 'accepted':
          return this.$t("nurseCall.statusAccepted");
        case 'cancelled':
          return this.$t("nurseCall.statusCancelled");
        case 'pending':
          return this.$t("nurseCall.statusPending");
        default:
          return response || "";
      }
    },
    getResponseColor(response) {
      switch(response?.toLowerCase()) {
        case 'accepted':
          return "green";
        case 'cancelled':
          return "red";
        case 'pending':
          return "orange";
        default:
          return "grey";
      }
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';
      const date = new Date(dateTimeString);
      return date.toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.v-card {
  border-radius: 10px;
}
</style>